/**
 * Buttress PDP — Model + Color → native cart (matches live site UX)
 */
(function () {
  /** Swatch display order — Beige, Charcoal, Black, Mocha, then remaining Shopify colors. */
  var COLOR_DISPLAY_ORDER = ['Beige', 'Charcoal', 'Black', 'Mocha'];
  /** Preferred default selection when the current color isn't available for a model. */
  var COLOR_DEFAULT_PREF = ['Beige', 'Charcoal', 'Black', 'Mocha'];

  function money(cents, currency) {
    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: currency || 'USD',
      });
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function modelKey(sizeValue) {
    return String(sizeValue || '')
      .split('(')[0]
      .trim()
      .toUpperCase();
  }

  function sortColors(list) {
    return list.slice().sort(function (a, b) {
      var ia = COLOR_DISPLAY_ORDER.indexOf(a.color);
      var ib = COLOR_DISPLAY_ORDER.indexOf(b.color);
      if (ia === -1) ia = 999;
      if (ib === -1) ib = 999;
      if (ia !== ib) return ia - ib;
      return 0;
    });
  }

  class TbpProduct extends HTMLElement {
    connectedCallback() {
      this.data = window.TBP_PRODUCT;
      if (!this.data || !this.data.variants) return;

      this.sizeOption = this.data.options.find(function (o) {
        return ['size', 'model'].indexOf(String(o.name || '').toLowerCase()) >= 0;
      });
      this.colorOption = this.data.options.find(function (o) {
        return ['color', 'colour'].indexOf(String(o.name || '').toLowerCase()) >= 0;
      });
      if (!this.sizeOption || !this.colorOption) {
        console.warn('[tbp-product] Expected Model/Size and Color', this.data.options);
        return;
      }

      // Prefer URL ?model=odb|omg|ort, then ORT as the default model
      var params = new URLSearchParams(window.location.search);
      var modelParam = String(params.get('model') || params.get('variant') || '')
        .trim()
        .toUpperCase();
      var fromUrl = null;
      if (modelParam && ['ODB', 'OMG', 'ORT'].indexOf(modelParam) >= 0) {
        fromUrl = this.sizeOption.values.find(function (v) {
          return modelKey(v) === modelParam;
        });
      }
      var ort = this.sizeOption.values.find(function (v) {
        return modelKey(v) === 'ORT';
      });
      this.selectedSize =
        fromUrl || ort || this.data.selectedSize || this.sizeOption.values[0];
      // Prefer URL ?color=, else Beige (COLOR_DEFAULT_PREF via ensureColorForSize)
      var colorParam = String(params.get('color') || '').trim().toLowerCase();
      var fromColorUrl = null;
      if (colorParam) {
        fromColorUrl = (this.colorOption.values || []).find(function (c) {
          return String(c).trim().toLowerCase() === colorParam;
        });
      }
      var beige = (this.colorOption.values || []).find(function (c) {
        return String(c).trim().toLowerCase() === 'beige';
      });
      this.selectedColor = fromColorUrl || beige || this.data.selectedColor || null;
      this.mediaIndex = 0;

      this.els = {
        media: this.querySelector('[data-tbp-main-media]'),
        slider: this.querySelector('[data-tbp-slider]'),
        thumbs: this.querySelector('[data-tbp-thumbs]'),
        dots: this.querySelector('[data-tbp-dots]'),
        price: this.querySelector('[data-tbp-price]'),
        compare: this.querySelector('[data-tbp-compare]'),
        save: this.querySelector('[data-tbp-save]'),
        models: this.querySelector('[data-tbp-models]'),
        modelCard: this.querySelector('[data-tbp-model-card]'),
        colors: this.querySelector('[data-tbp-colors]'),
        variantInput: this.querySelector('input[name="id"]'),
        buyNow: this.querySelector('[data-tbp-buy-now]'),
        addCart: this.querySelector('[data-tbp-add-cart]'),
        sticky: this.querySelector('[data-tbp-sticky]'),
        stickyMeta: this.querySelector('[data-tbp-sticky-meta]'),
        stickyThumb: this.querySelector('[data-tbp-sticky-thumb]'),
        stickyAdd: this.querySelector('[data-tbp-sticky-add]'),
        buyBox: this.querySelector('[data-tbp-buy-box]'),
        error: this.querySelector('[data-tbp-error]'),
        roulette: this.querySelector('[data-tbp-roulette]'),
      };

      this.ensureColorForSize();
      this.bindSliderScroll();
      this.render();
      this.bindSticky();
      this.bindActions();
      this.startRoulette();
    }

    variantsForSize(size) {
      return this.data.variants.filter(function (v) {
        return v.size === size;
      });
    }

    findVariant(size, color) {
      return this.data.variants.find(function (v) {
        return v.size === size && v.color === color;
      });
    }

    ensureColorForSize() {
      var list = sortColors(this.variantsForSize(this.selectedSize));
      if (!list.length) return;
      var self = this;
      var next = list.find(function (v) {
        return v.color === self.selectedColor && v.available;
      });
      if (!next) {
        next = COLOR_DEFAULT_PREF.map(function (c) {
          return list.find(function (v) {
            return v.color === c && v.available;
          });
        }).find(Boolean);
      }
      if (!next) next = list.find(function (v) { return v.available; }) || list[0];
      this.selectedColor = next.color;
    }

    currentVariant() {
      return this.findVariant(this.selectedSize, this.selectedColor);
    }

    mediaList(variant) {
      // Prefer Next-site gallery (2 model shots + lifestyle) over Shopify product media
      if (typeof window.TBP_resolveGallery === 'function' && variant) {
        var resolved = window.TBP_resolveGallery(variant.size, variant.color);
        if (resolved && resolved.length) {
          return resolved.map(function (im) {
            return { jpg: im.jpg, webp: im.webp || null };
          });
        }
      }
      function asObj(u) {
        return typeof u === 'string' ? { jpg: u, webp: null } : u;
      }
      if (variant && variant.images && variant.images.length) {
        return variant.images.map(asObj);
      }
      if (variant && variant.featured_image) return [asObj(variant.featured_image)];
      return (this.data.images || []).map(asObj);
    }

    renderModels() {
      if (!this.els.models) return;
      var meta = this.data.modelMeta || {};
      var self = this;
      // Preferred display order ODB, OMG, ORT
      var values = this.sizeOption.values.slice().sort(function (a, b) {
        var order = { ODB: 1, OMG: 2, ORT: 3 };
        return (order[modelKey(a)] || 9) - (order[modelKey(b)] || 9);
      });

      this.els.models.innerHTML = values
        .map(function (size) {
          var key = modelKey(size);
          var info = meta[key] || {};
          var sample =
            self.variantsForSize(size).find(function (v) { return v.available; }) ||
            self.variantsForSize(size)[0];
          var priceLabel = sample ? money(sample.price, self.data.currency) : '';
          var badge = info.badge
            ? '<span class="tbp-pdp__best">' + info.badge + '</span>'
            : '';
          var selected = size === self.selectedSize ? ' is-selected' : '';
          return (
            '<button type="button" class="tbp-pdp__model' +
            selected +
            '" data-size="' +
            size.replace(/"/g, '&quot;') +
            '">' +
            badge +
            '<span class="tbp-pdp__model-name">' +
            (info.short || key) +
            '</span>' +
            '<span class="tbp-pdp__model-price">' +
            priceLabel +
            '</span></button>'
          );
        })
        .join('');

      this.els.models.querySelectorAll('[data-size]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.selectedSize = btn.getAttribute('data-size');
          self.mediaIndex = 0;
          self.ensureColorForSize();
          self.render();
        });
      });
    }

    renderColors() {
      if (!this.els.colors) return;
      var list = sortColors(this.variantsForSize(this.selectedSize));
      var self = this;
      this.els.colors.innerHTML = list
        .map(function (v) {
          var selected = v.color === self.selectedColor ? ' is-selected' : '';
          var sold = !v.available ? ' is-soldout' : '';
          var swatchSrc =
            typeof window.TBP_resolveSwatch === 'function'
              ? window.TBP_resolveSwatch(v.size, v.color)
              : null;
          if (!swatchSrc) {
            var first = (self.mediaList(v) || [])[0] || null;
            swatchSrc = first
              ? first.jpg || first.webp || null
              : v.featured_image || null;
          }
          if (swatchSrc && typeof swatchSrc === 'object') swatchSrc = swatchSrc.jpg || null;
          var img = swatchSrc
            ? '<img src="' +
              String(swatchSrc).replace(/"/g, '&quot;') +
              '" alt="' +
              v.color.replace(/"/g, '&quot;') +
              '" width="120" height="120" loading="lazy" decoding="async">'
            : '<span class="tbp-pdp__color-fallback">' + v.color + '</span>';
          var tag = !v.available
            ? '<span class="tbp-pdp__soldout-tag">SOLD<br>OUT</span>'
            : '';
          return (
            '<button type="button" class="tbp-pdp__color' +
            selected +
            sold +
            '" data-color="' +
            v.color.replace(/"/g, '&quot;') +
            '" ' +
            (!v.available ? 'aria-disabled="true"' : '') +
            '>' +
            img +
            tag +
            '</button>'
          );
        })
        .join('');

      this.els.colors.querySelectorAll('[data-color]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.getAttribute('aria-disabled') === 'true') return;
          self.selectedColor = btn.getAttribute('data-color');
          self.mediaIndex = 0;
          self.render();
        });
      });
    }

    bindSliderScroll() {
      var self = this;
      if (!this.els.slider || this._sliderBound) return;
      this._sliderBound = true;
      var ticking = false;
      this.els.slider.addEventListener(
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            ticking = false;
            var el = self.els.slider;
            if (!el) return;
            var w = el.clientWidth || 1;
            var idx = Math.round(el.scrollLeft / w);
            var images = self.mediaList(self.currentVariant());
            if (idx < 0) idx = 0;
            if (idx >= images.length) idx = images.length - 1;
            if (idx === self.mediaIndex) return;
            self.mediaIndex = idx;
            self.syncDotsAndThumbs(images);
          });
        },
        { passive: true }
      );
    }

    syncDotsAndThumbs(images) {
      var self = this;
      if (this.els.dots) {
        this.els.dots.querySelectorAll('[data-idx]').forEach(function (btn) {
          var i = parseInt(btn.getAttribute('data-idx'), 10) || 0;
          btn.classList.toggle('is-active', i === self.mediaIndex);
        });
      }
      if (this.els.thumbs) {
        this.els.thumbs.querySelectorAll('[data-idx]').forEach(function (btn) {
          var i = parseInt(btn.getAttribute('data-idx'), 10) || 0;
          btn.classList.toggle('is-active', i === self.mediaIndex);
        });
      }
      var main = images[this.mediaIndex] || images[0];
      if (this.els.stickyThumb && main) {
        this.els.stickyThumb.innerHTML =
          (main.webp
            ? '<picture><source type="image/webp" srcset="' + main.webp + '">'
            : '') +
          '<img src="' +
          main.jpg +
          '" alt="" width="80" height="80" decoding="async">' +
          (main.webp ? '</picture>' : '');
      }
    }

    goToSlide(index, smooth) {
      var images = this.mediaList(this.currentVariant());
      if (!images.length) return;
      if (index < 0) index = 0;
      if (index >= images.length) index = images.length - 1;
      this.mediaIndex = index;
      var el = this.els.slider;
      if (el) {
        var w = el.clientWidth || 1;
        el.scrollTo({
          left: index * w,
          behavior: smooth ? 'smooth' : 'auto',
        });
      }
      this.syncDotsAndThumbs(images);
    }

    renderMedia(variant) {
      var images = this.mediaList(variant);
      if (!images.length && this.data.featured_image) {
        images = [{ jpg: this.data.featured_image, webp: null }];
      }
      if (this.mediaIndex >= images.length) this.mediaIndex = 0;
      var self = this;

      function slideHtml(im, i, eager) {
        if (!im || !im.jpg) return '';
        var loading = eager ? 'eager' : 'lazy';
        var fetchp = eager ? ' fetchpriority="high"' : '';
        var img =
          '<img src="' +
          im.jpg +
          '" alt="' +
          self.data.title +
          ' - view ' +
          (i + 1) +
          '" width="1000" height="1000" loading="' +
          loading +
          '" decoding="async"' +
          fetchp +
          '>';
        if (im.webp) {
          img =
            '<picture><source type="image/webp" srcset="' +
            im.webp +
            '">' +
            img +
            '</picture>';
        }
        return '<div class="tbp-pdp__slide">' + img + '</div>';
      }

      if (this.els.slider) {
        this.els.slider.innerHTML = images
          .map(function (im, i) {
            return slideHtml(im, i, i === 0);
          })
          .join('');
        this.els.slider.classList.toggle('is-single', images.length <= 1);
        // Jump to current index without animation (variant change)
        var w = this.els.slider.clientWidth || 1;
        this.els.slider.scrollLeft = this.mediaIndex * w;
      }

      if (this.els.thumbs) {
        this.els.thumbs.innerHTML = images
          .slice(0, 5)
          .map(function (im, i) {
            var thumb =
              (im.webp
                ? '<picture><source type="image/webp" srcset="' + im.webp + '">'
                : '') +
              '<img src="' +
              im.jpg +
              '" alt="" width="160" height="160" loading="lazy" decoding="async">' +
              (im.webp ? '</picture>' : '');
            return (
              '<button type="button" class="tbp-pdp__thumb' +
              (i === self.mediaIndex ? ' is-active' : '') +
              '" data-idx="' +
              i +
              '">' +
              thumb +
              '</button>'
            );
          })
          .join('');
        this.els.thumbs.querySelectorAll('[data-idx]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            self.goToSlide(parseInt(btn.getAttribute('data-idx'), 10) || 0, true);
          });
        });
      }

      if (this.els.dots) {
        if (images.length <= 1) {
          this.els.dots.innerHTML = '';
          this.els.dots.hidden = true;
        } else {
          this.els.dots.hidden = false;
          this.els.dots.innerHTML = images
            .map(function (src, i) {
              return (
                '<button type="button" class="tbp-pdp__dot' +
                (i === self.mediaIndex ? ' is-active' : '') +
                '" data-idx="' +
                i +
                '" aria-label="Go to image ' +
                (i + 1) +
                '"></button>'
              );
            })
            .join('');
          this.els.dots.querySelectorAll('[data-idx]').forEach(function (btn) {
            btn.addEventListener('click', function () {
              self.goToSlide(parseInt(btn.getAttribute('data-idx'), 10) || 0, true);
            });
          });
        }
      }

      this.syncDotsAndThumbs(images);
    }

    render() {
      var variant = this.currentVariant();
      this.renderModels();
      this.renderColors();
      this.renderMedia(variant);

      var key = modelKey(this.selectedSize);
      var info = (this.data.modelMeta || {})[key] || {};
      if (this.els.modelCard) {
        this.els.modelCard.innerHTML =
          '<div><strong>' +
          (info.short || key) +
          ' — ' +
          (info.full || this.selectedSize) +
          '</strong></div>' +
          '<div class="muted"><strong>Size</strong> · ' +
          (info.dims || '—') +
          ' · ' +
          (info.weight || '') +
          '</div>' +
          '<div class="muted"><strong>Butt type</strong> · ' +
          (info.vibe || '') +
          '</div>' +
          (info.bestFor
            ? '<div class="muted"><strong>Best for</strong> · ' + info.bestFor + '</div>'
            : '');
      }

      if (!variant) return;

      if (this.els.variantInput) {
        this.els.variantInput.value = variant.id;
        this.els.variantInput.disabled = !variant.available;
      }
      if (this.els.price) {
        this.els.price.textContent = money(variant.price, this.data.currency);
      }
      if (this.els.compare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.els.compare.style.display = '';
          this.els.compare.textContent = money(
            variant.compare_at_price,
            this.data.currency,
          );
        } else {
          this.els.compare.style.display = 'none';
        }
      }
      if (this.els.save) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          var saveAmt = variant.compare_at_price - variant.price;
          this.els.save.style.display = '';
          this.els.save.textContent = 'Save ' + money(saveAmt, this.data.currency);
        } else {
          this.els.save.style.display = 'none';
        }
      }

      var disabled = !variant.available;
      var label = disabled ? 'Sold Out' : null;
      [this.els.buyNow, this.els.addCart, this.els.stickyAdd].forEach(function (el) {
        if (!el) return;
        el.disabled = disabled;
      });
      if (this.els.buyNow) {
        this.els.buyNow.innerHTML = disabled
          ? 'Sold Out<small>Pick another color above</small>'
          : 'Buy Now<small>Skip cart · checkout now</small>';
      }
      if (this.els.addCart) {
        this.els.addCart.textContent = disabled ? 'Sold Out' : 'Add to Cart';
      }
      if (this.els.stickyAdd) {
        this.els.stickyAdd.textContent = disabled ? 'Sold Out' : 'Add to Cart';
      }
      if (this.els.stickyMeta) {
        this.els.stickyMeta.innerHTML =
          '<strong>' +
          key +
          ' · ' +
          this.selectedColor +
          '</strong><span>' +
          money(variant.price, this.data.currency) +
          '</span>';
      }

      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url);
      } catch (e) {}
    }

    bindSticky() {
      if (!this.els.buyBox || !this.els.sticky) return;
      var sticky = this.els.sticky;
      var io = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          // Show when buy box scrolled above viewport (matching live site)
          var above = entry.boundingClientRect.bottom < 0;
          sticky.classList.toggle('is-visible', above);
        },
        { threshold: 0 },
      );
      io.observe(this.els.buyBox);
    }

    startRoulette() {
      var quotes = this.data.roulette || [];
      if (!this.els.roulette || !quotes.length) return;
      var i = 0;
      var el = this.els.roulette;
      var MAX_CHARS = 160;

      function escapeHtml(s) {
        return String(s || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      function truncateQuote(text) {
        var t = String(text || '').replace(/\s+/g, ' ').trim();
        if (t.length <= MAX_CHARS) return t;
        var cut = t.slice(0, MAX_CHARS);
        var lastSpace = cut.lastIndexOf(' ');
        if (lastSpace > MAX_CHARS * 0.6) cut = cut.slice(0, lastSpace);
        return cut.replace(/[.,;:!?–—-]+$/, '') + '…';
      }

      function paint() {
        var q = quotes[i % quotes.length];
        el.innerHTML =
          '<div class="tbp-pdp__roulette-stars" aria-hidden="true">★★★★★</div>' +
          '<p class="tbp-pdp__roulette-quote">“' +
          escapeHtml(truncateQuote(q.text)) +
          '”</p>' +
          '<p class="tbp-pdp__roulette-author">— ' +
          escapeHtml(q.author) +
          '</p>';
        i += 1;
      }
      paint();
      setInterval(function () {
        if (document.visibilityState !== 'visible') return;
        el.classList.add('is-fading');
        setTimeout(function () {
          paint();
          el.classList.remove('is-fading');
        }, 300);
      }, 7500);
    }

    showError(msg) {
      if (!this.els.error) return;
      this.els.error.textContent = msg || '';
      this.els.error.classList.toggle('is-visible', Boolean(msg));
    }

    async addToCart(goCheckout) {
      var variant = this.currentVariant();
      if (!variant || !variant.available) {
        this.showError('This combination is sold out.');
        return;
      }
      this.showError('');
      var busyBtns = [this.els.buyNow, this.els.addCart, this.els.stickyAdd].filter(Boolean);
      busyBtns.forEach(function (el) {
        el.disabled = true;
        el.setAttribute('aria-busy', 'true');
      });
      if (goCheckout && this.els.buyNow) {
        this.els.buyNow.innerHTML = 'Heading to checkout…';
      }
      // When checkout navigation starts, skip the post-ATC full re-render.
      // Rebuilding gallery/swatch <img>s mid-navigation makes Safari abort those
      // requests and leave white broken-"?" placeholders (also on Back).
      var shouldRerender = true;
      var checkoutStarted = false;
      try {
        if (window.TBP && typeof window.TBP.syncCartAttributes === 'function') {
          await window.TBP.syncCartAttributes();
        }

        var trackItem = {
          id: this.data.id,
          productId: this.data.id,
          variantId: variant.id,
          title: this.data.title,
          price: variant.price,
          quantity: 1,
          unit: 'cents',
        };
        // Cart totals win inside prepareAndTrackCheckout — don't stub a single-line value.
        // content_ids use product id for Meta catalog consistency with Purchase.
        var icOpts = {
          contentIds: [String(this.data.id)],
          unit: 'cents',
        };

        if (window.TBP && typeof window.TBP.addToCartAndRefresh === 'function') {
          var result = await window.TBP.addToCartAndRefresh(
            { id: variant.id, quantity: 1 },
            {
              checkout: !!goCheckout,
              ic: icOpts,
              beforeCheckout: function () {
                if (window.TBP && typeof window.TBP.trackAddToCart === 'function') {
                  window.TBP.trackAddToCart(trackItem);
                }
              },
            }
          );
          if (goCheckout && result && result.editorBlocked) {
            this.showError(
              'Checkout can’t open inside the theme editor. Use Preview (eye icon) or the live storefront — item was added to cart.'
            );
          } else if (goCheckout) {
            shouldRerender = false;
            checkoutStarted = true;
          } else if (window.TBP && typeof window.TBP.trackAddToCart === 'function') {
            window.TBP.trackAddToCart(trackItem);
          }
        } else {
          var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
          if (addUrl.indexOf('.js') === -1) addUrl += '.js';
          var res = await fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: variant.id, quantity: 1 }),
          });
          if (!res.ok) {
            var err = await res.json().catch(function () { return {}; });
            throw new Error(err.description || 'Could not add to cart');
          }
          if (window.TBP && typeof window.TBP.trackAddToCart === 'function') {
            window.TBP.trackAddToCart(trackItem);
          }
          if (goCheckout) {
            if (window.TBP && typeof window.TBP.goToCheckout === 'function') {
              var nav = await window.TBP.goToCheckout({ ic: icOpts });
              if (nav && nav.editorBlocked) {
                this.showError(
                  'Checkout can’t open inside the theme editor. Use Preview (eye icon) or the live storefront — item was added to cart.'
                );
              } else {
                shouldRerender = false;
                checkoutStarted = true;
              }
            } else {
              window.location.href = '/checkout';
              shouldRerender = false;
              checkoutStarted = true;
            }
          } else if (window.TBP && typeof window.TBP.refreshCartDrawer === 'function') {
            // Fallback ATC path: still open the drawer when helpers exist.
            await window.TBP.refreshCartDrawer({ open: true });
          }
        }
      } catch (e) {
        this.showError(e.message || 'Could not add to cart');
      } finally {
        busyBtns.forEach(function (el) {
          el.removeAttribute('aria-busy');
        });
        if (shouldRerender) this.render();
      }
    }

    bindActions() {
      var self = this;
      if (this.els.addCart) {
        this.els.addCart.addEventListener('click', function (e) {
          e.preventDefault();
          self.addToCart(false);
        });
      }
      if (this.els.stickyAdd) {
        this.els.stickyAdd.addEventListener('click', function (e) {
          e.preventDefault();
          self.addToCart(false);
        });
      }
      if (this.els.buyNow) {
        this.els.buyNow.addEventListener('click', function (e) {
          e.preventDefault();
          self.addToCart(true);
        });
      }
    }
  }

  if (!customElements.get('tbp-product')) {
    customElements.define('tbp-product', TbpProduct);
  }

  // Demo + influencer video lightbox (single handler)
  document.addEventListener('click', function (e) {
    var demoPlay = e.target.closest('[data-tbp-demo-play]');
    if (demoPlay) {
      e.preventDefault();
      var wrap = demoPlay.closest('[data-tbp-demo-video]');
      var video = wrap && wrap.querySelector('[data-tbp-demo-el]');
      if (!wrap || !video || wrap.classList.contains('is-playing')) return;
      var mobile = window.matchMedia('(max-width: 749px)').matches;
      var src = mobile
        ? video.getAttribute('data-src-mobile')
        : video.getAttribute('data-src-desktop');
      if (!src) return;
      wrap.classList.add('is-playing');
      video.removeAttribute('hidden');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.controls = true;
      video.src = src;
      video.load();
      var start = function () {
        video.play().catch(function () {});
      };
      if (video.readyState >= 2) start();
      else video.addEventListener('loadeddata', start, { once: true });
      return;
    }

    var card = e.target.closest('[data-tbp-open-video]');
    if (card) {
      e.preventDefault();
      var url = card.getAttribute('data-tbp-open-video');
      var modal = document.querySelector('[data-tbp-video-modal]');
      var videoEl = modal && modal.querySelector('video');
      if (!url || !modal || !videoEl) return;
      // Keep lightbox above theme chrome / sticky bars
      if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }
      videoEl.setAttribute('playsinline', '');
      videoEl.setAttribute('webkit-playsinline', '');
      videoEl.controls = true;
      videoEl.src = url;
      videoEl.load();
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      var playModal = function () {
        videoEl.play().catch(function () {});
      };
      // Start only after the modal is painted so iOS shows frames (not audio-only)
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (videoEl.readyState >= 2) playModal();
          else videoEl.addEventListener('loadeddata', playModal, { once: true });
        });
      });
      return;
    }

    var closeBtn = e.target.closest('[data-tbp-close-video], [data-tbp-video-close]');
    var backdrop = e.target.matches('[data-tbp-video-modal]') ? e.target : null;
    if (closeBtn || backdrop) {
      var modal2 = closeBtn
        ? closeBtn.closest('[data-tbp-video-modal]') || document.querySelector('[data-tbp-video-modal]')
        : backdrop;
      if (!modal2) return;
      var video2 = modal2.querySelector('video');
      modal2.classList.remove('is-open');
      modal2.setAttribute('aria-hidden', 'true');
      if (video2) {
        video2.pause();
        video2.removeAttribute('src');
        video2.load();
      }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var openModal = document.querySelector('[data-tbp-video-modal].is-open');
    if (!openModal) return;
    var v = openModal.querySelector('video');
    openModal.classList.remove('is-open');
    openModal.setAttribute('aria-hidden', 'true');
    if (v) {
      v.pause();
      v.removeAttribute('src');
      v.load();
    }
  });

  // Safari bfcache: if Buy Now left aborted image loads, reload broken ones on Back.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document
      .querySelectorAll(
        [
          'tbp-product img',
          '[data-tbp-accessory] img',
          '.tbp-pdp__gallery img',
          '.tbp-pdp__colors img',
          '.tbp-pdp__thumbs img',
          '[data-acc-slider] img',
          '[data-acc-thumbs] img',
          '[data-acc-colors] img',
        ].join(', '),
      )
      .forEach(function (img) {
        if (img.complete && img.naturalWidth === 0 && img.src) {
          var src = img.src;
          img.removeAttribute('src');
          img.src = src;
        }
      });
  });
})();
